import type { Zone } from '@/types/zones/zone';
import { getProductContext } from '@/logic/app-data/get-product-context';
import { getPlaytechGameConfig, findPlaytechGameInZones, PLAYTECH_FALLBACK_GAME } from './find-playtech-game';
import type { PlaytechGameConfig } from './find-playtech-game';
import { ddRumError } from '@/utils/datadog';
import { ddErrorPokerGameConfigFallback } from '@/utils/datadog/poker';

/**
 * Finds the first Playtech game in the lobby zones. Always reads product context
 * for the homepage route because Playtech games live in lobby zones regardless of
 * which page the caller is on — e.g. the rewards page has no Playtech game data
 * in its own zones.
 * Returns PLAYTECH_FALLBACK_GAME when no Playtech game is found.
 */
export const fetchPlaytechGameConfig = async (isMobile: boolean): Promise<PlaytechGameConfig> => {
    const productContext = await getProductContext('/');
    const zones = productContext?.zones;

    if (!zones) {
        ddRumError(ddErrorPokerGameConfigFallback({ reason: 'no_zones' }));
        return PLAYTECH_FALLBACK_GAME;
    }

    const standardZones = zones.filter((zone): zone is Zone => Array.isArray((zone as Zone)?.data?.body));

    const game = findPlaytechGameInZones(standardZones);

    if (game === null) {
        ddRumError(ddErrorPokerGameConfigFallback({ reason: 'no_playtech_game' }));
        return PLAYTECH_FALLBACK_GAME;
    }

    return getPlaytechGameConfig(game, isMobile);
};
